import * as React from 'react';
import type { HighlightRequest, HighlightResponse, Span } from './highlight.worker';

/* ---------------------------------------------------------------------------
   The main-thread half of the highlighter.

   It owns one worker for the lifetime of the mounted viewer, an unbounded-per-
   diff cache keyed by row, and a request coalescer. The contract with the
   renderer is deliberately one-way and never blocking: ask for a window, paint
   whatever is already cached, and re-render when the answer lands. A row with no
   tokens yet renders as plain mono ink, which is correct and readable — the
   highlighting is an aid, never a prerequisite.

   Coalescing matters more than the tokeniser does. Scrolling a 40,000-line diff
   fires a scroll handler dozens of times a second, and posting one message per
   frame per row would spend more time in structured-clone than in tokenising. So
   requests are batched per animation frame, deduplicated against both the cache
   and the in-flight set, and capped per batch.

   If the worker cannot be constructed at all — an environment that blocks module
   workers — the hook degrades to no highlighting rather than to main-thread
   tokenising. A diff that is readable but flat beats a diff that janks.
--------------------------------------------------------------------------- */

export type { Span };

export interface HighlightItem {
  key: string;
  language: string | null;
  content: string;
}

const MAX_BATCH = 400;

export interface Highlighter {
  /** Cached spans for a row, or null if it has not been tokenised yet. */
  get: (key: string) => Span[] | null;
  /** Queue a window for tokenising. Cheap to call on every scroll frame. */
  request: (items: readonly HighlightItem[]) => void;
  /** Drop everything — a new diff target or a re-fetch. */
  clear: () => void;
  ready: boolean;
}

export function useHighlighter(): Highlighter {
  const workerRef = React.useRef<Worker | null>(null);
  const cacheRef = React.useRef<Map<string, Span[]>>(new Map());
  const pendingRef = React.useRef<Map<string, HighlightItem>>(new Map());
  const inFlightRef = React.useRef<Set<string>>(new Set());
  const frameRef = React.useRef<number | null>(null);
  const [ready, setReady] = React.useState(false);
  // Bumped when a batch lands, to re-render the rows that were waiting.
  const [, setVersion] = React.useState(0);

  React.useEffect(() => {
    let worker: Worker | null = null;
    try {
      worker = new Worker(new URL('./highlight.worker.ts', import.meta.url), {
        type: 'module',
        name: 'hourz-diff-highlighter',
      });
    } catch {
      // No worker available. The viewer stays fully functional without colour.
      return;
    }

    worker.addEventListener('message', (event: MessageEvent<HighlightResponse>) => {
      const data = event.data;
      if (!data || data.type !== 'tokens') return;
      for (const result of data.results) {
        cacheRef.current.set(result.key, result.spans);
        inFlightRef.current.delete(result.key);
      }
      setVersion((v) => v + 1);
    });

    workerRef.current = worker;
    setReady(true);

    return () => {
      worker.terminate();
      workerRef.current = null;
      setReady(false);
    };
  }, []);

  const flush = React.useCallback(() => {
    frameRef.current = null;
    const worker = workerRef.current;
    if (!worker) {
      pendingRef.current.clear();
      return;
    }
    const batch: HighlightItem[] = [];
    for (const [key, item] of pendingRef.current) {
      if (batch.length >= MAX_BATCH) break;
      batch.push(item);
      inFlightRef.current.add(key);
    }
    for (const item of batch) pendingRef.current.delete(item.key);

    if (batch.length === 0) return;
    const message: HighlightRequest = { type: 'highlight', batch };
    worker.postMessage(message);

    // Anything the cap left behind is sent on the next frame rather than
    // dropped, so a fast scroll through a huge file still ends up tokenised.
    if (pendingRef.current.size > 0) {
      frameRef.current = requestAnimationFrame(flush);
    }
  }, []);

  React.useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const request = React.useCallback(
    (items: readonly HighlightItem[]) => {
      if (!workerRef.current) return;
      let queued = false;
      for (const item of items) {
        if (cacheRef.current.has(item.key)) continue;
        if (inFlightRef.current.has(item.key)) continue;
        if (pendingRef.current.has(item.key)) continue;
        pendingRef.current.set(item.key, item);
        queued = true;
      }
      if (queued && frameRef.current === null) {
        frameRef.current = requestAnimationFrame(flush);
      }
    },
    [flush],
  );

  const get = React.useCallback((key: string) => cacheRef.current.get(key) ?? null, []);

  const clear = React.useCallback(() => {
    cacheRef.current.clear();
    pendingRef.current.clear();
    inFlightRef.current.clear();
    setVersion((v) => v + 1);
  }, []);

  return React.useMemo(() => ({ get, request, clear, ready }), [get, request, clear, ready]);
}

/**
 * Split a line into styled segments using the worker's spans.
 *
 * Returns plain text as one segment when there are no spans, which is both the
 * "not tokenised yet" case and the "nothing to highlight" case. The renderer
 * cannot tell them apart and does not need to.
 */
export function segmentLine(content: string, spans: Span[] | null): { text: string; cls: string | null }[] {
  if (!spans || spans.length === 0) return [{ text: content, cls: null }];

  const out: { text: string; cls: string | null }[] = [];
  let cursor = 0;
  for (const [start, end, cls] of spans) {
    if (start > cursor) out.push({ text: content.slice(cursor, start), cls: null });
    if (end > start) out.push({ text: content.slice(start, end), cls: `syn-${cls}` });
    cursor = Math.max(cursor, end);
  }
  if (cursor < content.length) out.push({ text: content.slice(cursor), cls: null });
  return out;
}
